/**
 * O canal transiente `buscaStreaming` → a lista que a tela desenha
 * (spec-001 `pesquisa-internet-chat`, [T101] e [T106]).
 *
 * ## De onde vem a entrada
 *
 * Um array PLANO de payloads `d_busca`, na ordem de chegada — o que
 * `store/mutations.js::ADICIONAR_BUSCA_STREAMING` acumula a partir do handler
 * `busca` (`api/chat-event-handlers.js`). O contrato é
 * `contratos/events_agente.schema.json` → `$defs.d_busca`:
 *
 *     { fase: 'consulta'|'resultado', caminho: 'server_tool'|'harness'|'gateway',
 *       consulta?: string, fontes?: Citacao[],
 *       motivo?: string, ofensor?: string, permitido?: string }
 *
 * ⚠️ `motivo`/`ofensor`/`permitido` **existem no produtor e NÃO estão
 * declarados no `d_busca`** — quem os monta é `eventos_harness.py::
 * montar_evento_busca` (worker), e o schema não os lista nem os proíbe
 * (`d_busca` não tem `additionalProperties: false`). São lidos aqui porque sem
 * eles uma busca NEGADA fica eternamente "pesquisando..." na tela.
 *
 * ## O que esta função resolve, e por quê ela existe separada do componente
 *
 * Três coisas que o template não pode fazer:
 *
 * 1. **Parear `consulta` com `resultado`.** O evento não carrega id nenhum —
 *    nem `toolCallId`, nem o `seq` do envelope (o desembrulho entrega só
 *    `data` ao store). O pareamento é POSICIONAL: o `resultado` fecha a busca
 *    ABERTA mais antiga do MESMO `caminho` (FIFO). Com tool-use paralelo do
 *    modelo (`consulta1, consulta2, resultado1, resultado2`) o FIFO acerta; o
 *    LIFO trocaria as consultas de lugar.
 * 2. **Distinguir as três falhas da US-8** — ver `estadoDoResultado` abaixo.
 * 3. **Não deixar busca nenhuma calada.** Todo evento vira linha: a recusa
 *    também, com o motivo dito ao consultor.
 */
import { MOTIVO_TETO_DE_BUSCAS_DO_TURNO } from '@modules/aawz-intelligence-v2/constants/Busca'

export const FASE_CONSULTA = 'consulta'
export const FASE_RESULTADO = 'resultado'

/** A busca saiu e o resultado ainda não voltou. */
export const ESTADO_EM_ANDAMENTO = 'em_andamento'
/** Voltou com ao menos uma fonte — o caso feliz da US-6. */
export const ESTADO_COM_FONTES = 'com_fontes'
/** [US-8] A leitura de uma PÁGINA falhou — e dá para dizer qual. */
export const ESTADO_PAGINA_NAO_LIDA = 'pagina_nao_lida'
/** [US-8] A busca voltou vazia — não há página para nomear. */
export const ESTADO_SEM_RESULTADO = 'sem_resultado'
/** [US-8] O teto de buscas do turno acabou. */
export const ESTADO_TETO_ATINGIDO = 'teto_atingido'
/** A guarda recusou a consulta ANTES de ela sair para o provedor. */
export const ESTADO_RECUSADA = 'recusada'

/**
 * Motivos que significam "o teto acabou", não "a consulta foi recusada".
 *
 * Publicado pelo worker desde `acf11821` (`busca_hook.py:511`). Motivo fora
 * deste conjunto cai em `ESTADO_RECUSADA` — nunca fica calado, e nunca vira
 * teto por engano.
 */
const MOTIVOS_DE_TETO = [MOTIVO_TETO_DE_BUSCAS_DO_TURNO]

/**
 * Um endereço de página, e não um termo de busca.
 *
 * **Não é heurística sobre a forma — é o contrato do produtor.** Quem monta a
 * `consulta` é `agent_sdk/stream.py:337` e `:371`, com
 * `entrada.get('query') or entrada.get('url', '')`: `WebSearch`/`web_search`
 * só têm `query`, `WebFetch`/`web_fetch` só têm `url`. Uma `consulta` que é
 * endereço absoluto veio, portanto, de uma LEITURA DE PÁGINA.
 *
 * ⚠️ A informação chega ao front DEGRADADA: o evento não diz qual tool rodou.
 * O dia em que o worker carregar isso no payload, este teste vira leitura de
 * campo e para de depender do formato do texto.
 *
 * @param {String} consulta O termo ou endereço da busca.
 * @returns {Boolean} `true` quando a busca era a leitura de uma página.
 */
export function ehLeituraDePagina(consulta) {
  return /^https?:\/\//i.test(String(consulta || '').trim())
}

/**
 * O estado de uma busca que VOLTOU — as duas primeiras frases da [US-8].
 *
 * A régua não é "deu erro": é **quantas fontes entraram na resposta**. Zero
 * fonte com endereço ⇒ a página não foi lida (e o consultor tem direito de
 * saber QUAL). Zero fonte com termo ⇒ a busca não encontrou nada, e o que
 * vier a seguir não veio da internet.
 *
 * @param {Array} fontes As citações que o evento trouxe.
 * @param {String} consulta O termo/endereço pareado.
 * @returns {String} Um dos `ESTADO_*`.
 */
export function estadoDoResultado(fontes, consulta) {
  if (fontes && fontes.length > 0) return ESTADO_COM_FONTES
  if (ehLeituraDePagina(consulta)) return ESTADO_PAGINA_NAO_LIDA
  return ESTADO_SEM_RESULTADO
}

/**
 * O estado de uma busca NEGADA, pelo motivo que o worker publicou.
 *
 * @param {String} motivo O `motivo` do payload.
 * @returns {String} `ESTADO_TETO_ATINGIDO` ou `ESTADO_RECUSADA`.
 */
export function estadoDaNegacao(motivo) {
  if (MOTIVOS_DE_TETO.indexOf(motivo) !== -1) return ESTADO_TETO_ATINGIDO
  return ESTADO_RECUSADA
}

/**
 * Fontes sem repetição, por URL.
 *
 * O worker emite uma citação por resultado e a mesma página pode voltar duas
 * vezes na MESMA busca (`_citacoes_de_conteudo` não dedupa). Duas linhas
 * idênticas no cartão leem como duas fontes distintas — afirmação falsa sobre
 * a procedência.
 *
 * @param {Array} fontes As citações cruas do evento.
 * @returns {Array} As citações com URL única, na ordem de chegada.
 */
function fontesSemRepeticao(fontes) {
  const vistas = {}
  return (fontes || []).filter(function (fonte) {
    const url = (fonte && fonte.url) || ''
    if (!url || vistas[url]) return false
    vistas[url] = true
    return true
  })
}

function novaBusca(payload, estado) {
  return {
    consulta: (payload && payload.consulta) || '',
    caminho: (payload && payload.caminho) || '',
    estado: estado,
    fontes: [],
    motivo: (payload && payload.motivo) || '',
    ofensor: (payload && payload.ofensor) || '',
    permitido: (payload && payload.permitido) || '',
    // A guarda barrou esta busca antes de ela sair. É o que autoriza a
    // absorção do `resultado` vazio que vem logo atrás — ver `aplicarResultado`.
    negada: false,
  }
}

/**
 * A busca ABERTA mais antiga do mesmo caminho (FIFO) — ver o cabeçalho.
 *
 * @param {Array} buscas As buscas montadas até aqui.
 * @param {String} caminho O `caminho` do evento de resultado.
 * @returns {Object|null} A busca a fechar, ou `null` quando não há par.
 */
function buscaAbertaDoCaminho(buscas, caminho) {
  for (let i = 0; i < buscas.length; i += 1) {
    const candidata = buscas[i]
    if (candidata.estado === ESTADO_EM_ANDAMENTO && candidata.caminho === caminho) {
      return candidata
    }
  }
  return null
}

/**
 * A busca aberta cujo TERMO é o da negação — o pareamento exato.
 *
 * Desde `f3a0c5f8` o worker manda a `consulta` junto da negação POR TETO (e só
 * dela: nos outros caminhos o termo É o ofensor e fica de fora de propósito).
 * Com o termo, a negação encontra a própria busca em vez de fechar a mais
 * antiga — que é o que quebrava com duas buscas em voo.
 *
 * @param {Array} buscas As buscas montadas até aqui.
 * @param {String} caminho O `caminho` do evento.
 * @param {String} consulta O termo publicado na negação.
 * @returns {Object|null} A busca que casa, ou `null`.
 */
function buscaAbertaPorConsulta(buscas, caminho, consulta) {
  for (let i = 0; i < buscas.length; i += 1) {
    const candidata = buscas[i]
    if (
      candidata.estado === ESTADO_EM_ANDAMENTO &&
      candidata.caminho === caminho &&
      candidata.consulta === consulta
    ) {
      return candidata
    }
  }
  return null
}

/**
 * A última busca registrada do mesmo caminho — a candidata a ter gerado o eco.
 *
 * @param {Array} buscas As buscas montadas até aqui.
 * @param {String} caminho O `caminho` do evento.
 * @returns {Object|null} A última do caminho, ou `null`.
 */
function ultimaBuscaDoCaminho(buscas, caminho) {
  for (let i = buscas.length - 1; i >= 0; i -= 1) {
    if (buscas[i].caminho === caminho) return buscas[i]
  }
  return null
}

/**
 * O `resultado` vazio que vem logo atrás de uma negação é ECO dela, não busca.
 *
 * ★ Medido no worker (`acf11821`): barrar a busca **não** impede o par
 * `consulta`/`resultado` de sair. O `ToolUseBlock` já foi emitido quando o hook
 * `PreToolUse` decide, e o `tool_result` de erro que o `deny` produz volta com
 * `is_error` — então `_fontes_de_busca_harness` (`stream.py:252`) devolve `[]`.
 * Sem esta absorção o consultor lê DUAS frases sobre a mesma busca: "o limite
 * acabou" e, logo abaixo, "não encontrou resultados". A segunda é exatamente a
 * mentira que publicar a negação foi corrigir.
 *
 * Só o eco VAZIO é absorvido: `resultado` com fonte é procedência que entrou na
 * resposta, e engoli-lo apagaria da tela a origem de um número.
 *
 * @param {Array} buscas As buscas montadas até aqui.
 * @param {Array} fontes As fontes já deduplicadas do resultado.
 * @param {String} caminho O `caminho` do evento.
 * @returns {Boolean} `true` quando o evento deve ser descartado.
 */
function ehEcoDeNegacao(buscas, fontes, caminho) {
  if (fontes.length > 0) return false
  const ultima = ultimaBuscaDoCaminho(buscas, caminho)
  return Boolean(ultima && ultima.negada)
}

/**
 * A negação FECHA a busca aberta, em vez de abrir uma linha própria.
 *
 * Quem publica a negação é o hook (`busca_hook.py`), que **não tem o termo à
 * mão** — o payload vem sem `consulta`. Fechar a busca aberta é o que preserva
 * o termo: sem isso o consultor lê "o limite acabou" sem saber QUAL pergunta
 * ficou de fora.
 *
 * O pareamento tenta o TERMO primeiro (`buscaAbertaPorConsulta`) e só cai no
 * FIFO quando não há termo ou nenhum casa. Duas situações caem no FIFO, e as
 * duas são conhecidas:
 *
 * 1. **Negação sem termo** — identificador de cliente, domínio fora da
 *    allowlist, web desligada. Ali o termo É o ofensor e o produtor o omite de
 *    propósito (`busca_hook.py::_publicar_negacao`): publicá-lo poria na trilha
 *    do run exatamente o que a guarda existe para reter.
 * 2. **`consulta` ausente por reconexão** — o evento de consulta se perdeu e a
 *    negação chega sozinha.
 *
 * ⚠️ **O que NÃO está mais nesta lista, e vale registrar porque mudou duas
 * vezes:** a divergência entre o termo do fio e o da negação. Ela existiu por
 * PII — o fio publicava o cru, a negação o redigido — e foi fechada em duas
 * etapas no worker: `86bb506c` (redação estrutural no tradutor) e a injeção dos
 * sensíveis no `TradutorStream`, que cobriu identificador interno e valor de
 * patrimônio. Hoje as duas pontas redigem pelas MESMAS regras.
 *
 * Sobra uma assimetria medida e não fechada, do lado do produtor: o fio trunca
 * a consulta em 1000 (`stream.py:379`/`:421`) e a negação **não trunca**
 * (`eventos_harness.montar_evento_busca` só atribui). Termo acima de 1000
 * caracteres diverge — e o evento da negação viola o `maxLength: 1000` que o
 * próprio `d_busca` declara.
 *
 * (`WebFetch` era um terceiro caso e deixou de ser: `_consulta_visivel` passou
 * a ler `CAMPOS_DE_ENDERECO` ANTES dos de texto, então a negação carrega o
 * endereço — o mesmo que o fio publicou — e não o `prompt` do resumo.)
 *
 * Com uma busca em voo por vez, que é o caminho real, o FIFO não erra. Com duas
 * e uma negação sem termo, ele fecha a mais antiga e pode errar: está pinado em
 * teste, como está, e o que fecharia isso é `toolCallId` no `d_busca`.
 *
 * @param {Array} buscas As buscas montadas até aqui.
 * @param {Object} payload O payload da negação.
 */
function aplicarNegacao(buscas, payload) {
  const estado = estadoDaNegacao(payload.motivo)
  const caminho = (payload && payload.caminho) || ''
  const termo = (payload && payload.consulta) || ''
  const porTermo = termo ? buscaAbertaPorConsulta(buscas, caminho, termo) : null
  const aberta = porTermo || buscaAbertaDoCaminho(buscas, caminho)
  if (!aberta) {
    buscas.push(fecharComNegacao(novaBusca(payload, estado), payload, estado))
    return
  }
  fecharComNegacao(aberta, payload, estado)
}

/**
 * Grava a negação sobre a busca — extraído de `aplicarNegacao` pelo teto de
 * statements do ESLint do repo (14), não por estilo.
 *
 * @param {Object} alvo A busca a fechar.
 * @param {Object} payload O payload da negação.
 * @param {String} estado O `ESTADO_*` resolvido.
 * @returns {Object} O próprio `alvo`, para encadear no `push`.
 */
function fecharComNegacao(alvo, payload, estado) {
  // A consulta da NEGAÇÃO vence a do fio quando existe. ⚠️ **Hoje isto é um
  // no-op em produção**, e de propósito: as duas pontas do worker passaram a
  // redigir pelas mesmas regras (tradutor com os sensíveis injetados), então os
  // dois termos são iguais. Fica como defesa em profundidade — se um lado
  // regredir, a linha que o consultor lê e que persiste na mensagem continua
  // sendo a saneada. A barreira é do produtor; isto é o cinto.
  //
  // Quando o produtor não manda termo (os caminhos em que ele É o ofensor), a
  // do fio permanece: sobrescrever com vazio deixaria a linha muda.
  if (payload && payload.consulta) alvo.consulta = payload.consulta
  alvo.estado = estado
  alvo.negada = true
  alvo.motivo = payload.motivo
  alvo.ofensor = payload.ofensor || ''
  alvo.permitido = payload.permitido || ''
  return alvo
}

function aplicarResultado(buscas, payload) {
  const fontes = fontesSemRepeticao(payload.fontes)
  const caminho = (payload && payload.caminho) || ''
  if (ehEcoDeNegacao(buscas, fontes, caminho)) return
  const aberta = buscaAbertaDoCaminho(buscas, caminho)
  // Sem par (o `consulta` se perdeu numa reconexão, ou o produtor só emitiu o
  // resultado): a busca entra como linha PRÓPRIA, sem consulta. Descartar o
  // evento esconderia fontes que de fato entraram na resposta.
  const alvo = aberta || novaBusca(payload, ESTADO_EM_ANDAMENTO)
  alvo.fontes = fontes
  alvo.estado = estadoDoResultado(fontes, alvo.consulta)
  if (!aberta) buscas.push(alvo)
}

function aplicarEvento(buscas, payload) {
  if (!payload) return
  // A negação vem na fase `consulta` (`busca_hook.py:310`) e é terminal: não
  // existe `resultado` depois dela. Testada ANTES da fase, porque é o `motivo`
  // — não a fase — que decide.
  if (payload.motivo) {
    aplicarNegacao(buscas, payload)
    return
  }
  if (payload.fase === FASE_RESULTADO) {
    aplicarResultado(buscas, payload)
    return
  }
  if (payload.fase === FASE_CONSULTA) {
    buscas.push(novaBusca(payload, ESTADO_EM_ANDAMENTO))
  }
}

/**
 * O canal transiente inteiro → as buscas do turno, prontas para a tela.
 *
 * @param {Array} eventos Os payloads `d_busca` na ordem de chegada.
 * @returns {Array} Uma entrada por busca, com `estado`, `fontes` e `consulta`.
 */
export function buscasDoTurno(eventos) {
  const buscas = []
  ;(eventos || []).forEach(function (payload) {
    aplicarEvento(buscas, payload)
  })
  return buscas
}
